import { ILCHttpResponse, IPaginationRequest, IPaginationResponse, IUser } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import { IArchivedUserSummary } from '../pages/users/archivedUsers/archivedUsers.columns'
import { IUserSummary } from '../pages/users/usersList/users.columns'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/user`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    getAdmins: builder.query<IUser[], void>({
      query: () => {
        return {
          url: '/admins',
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IUser[]>) => {
        return rawResult.data as IUser[]
      },
    }),
    getUsers: builder.mutation<IPaginationResponse<IUserSummary[]>, IPaginationRequest>({
      query: ({ pageIndex, pageSize, sortBy, search }) => {
        return {
          url: '/multiple/pagination',
          method: 'POST',
          body: {
            pageIndex,
            pageSize,
            sortBy,
            search,
          },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IPaginationResponse<IUserSummary[]>>, meta) => {
        return rawResult.data as IPaginationResponse<IUserSummary[]>
      },
    }),
    getArchivedUsers: builder.mutation<IPaginationResponse<IArchivedUserSummary[]>, IPaginationRequest>({
      query: ({ pageIndex, pageSize, sortBy, search }) => {
        return {
          url: '/multiple/archived/pagination',
          method: 'POST',
          body: {
            pageIndex,
            pageSize,
            sortBy,
            search,
          },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IPaginationResponse<IArchivedUserSummary[]>>, meta) => {
        return rawResult.data as IPaginationResponse<IArchivedUserSummary[]>
      },
    }),
    resolveDisplayNames: builder.mutation<{ identity: string; fullName: string; inactive: boolean }[], string[]>({
      query: (identities) => {
        return {
          url: '/display-names',
          method: 'POST',
          body: { identities },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<{ identity: string; fullName: string; inactive: boolean }[]>) => {
        return rawResult.data ?? []
      },
    }),
    deleteUsers: builder.mutation<string[], string[]>({
      query: (identities) => {
        return {
          url: '/multiple',
          method: 'DELETE',
          body: {
            identities,
          },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<string[]>, meta) => {
        return rawResult.data as string[]
      },
    }),
  }),
})

export const {
  useGetAdminsQuery,
  useGetUsersMutation,
  useGetArchivedUsersMutation,
  useLazyGetAdminsQuery,
  useResolveDisplayNamesMutation,
} = usersApi
